<template>
  <div style="display: none">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import _sHelper from "@/services/HelperService";

export default {
  props: ["worker"],

  data() {
    return {
      personImage: null,
      qrCodeImage: null,
    };
  },
  props: {
    worker: Object,
    photoBorderType: {
      type: String,
      default: "rounded", //'rounded' o 'circle'
    },
    photoBorderColor: {
      type: String,
      default: "#F35217", //'#529518' o '#F35217'
    },
  },
  methods: {
    async generateImage() {
      try {
        // Obtener la imagen del trabajador
        await this.loadWorkerImage();

        // Generar QR code
        await this.generateQRCode();

        // Crear el canvas
        const canvas = this.$refs.canvas;
        if (!canvas) {
          throw new Error("Canvas no disponible");
        }

        const ctx = canvas.getContext("2d");

        // Dimensiones del fotocheck (600x1000px - optimizado para reducir peso)
        canvas.width = 600;
        canvas.height = 950;

        // Fondo blanco
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Logo Sunshine en la parte superior
        await this.drawLogo(ctx);

        // Foto del trabajador (circular)
        if (this.personImage) {
          await this.drawPhoto(ctx);
        }

        // Nombre del trabajador
        this.drawName(ctx);

        // Puesto/Cargo
        this.drawPosition(ctx);

        // Código QR
        if (this.qrCodeImage) {
          await this.drawQRCode(ctx);
        }

        // Barra naranja inferior
        this.drawBottomBar(ctx);

        // Convertir canvas a base64
        return canvas.toDataURL("image/png");
      } catch (error) {
        console.error("Error generando imagen del fotocheck:", error);
        this.$fun?.alert?.("Error al generar fotocheck: " + error.message, "error");
        return null;
      }
    },

    async loadWorkerImage() {
      return new Promise((resolve, reject) => {
        _sHelper
          .getPhotoWorkerBase64(this.worker.PrsDocumentNumber, this.$fun.getUserID())
          .then((rsp) => {
            if (rsp.status === 200) {
              this.personImage = "data:image/jpg;base64," + rsp.data;
              resolve();
            } else {
              reject("No se pudo cargar la imagen");
            }
          })
          .catch(reject);
      });
    },

    async generateQRCode() {
      // Generar QR code con los datos del trabajador (similar a FotoCheckPdf)
      try {
        const QRCode = require("qrcode");

        // Formatear nombre como en FotoCheckPdf
        let name = `${this.worker.NtpSurname || ""},${this.worker.NtpName || ""}`;
        let newName = "";
        for (let i = 0; i < name.length; i++) {
          if (name[i] === " ") {
            newName += " ";
          } else if (name[i].toLowerCase() === " ") {
            newName += ":";
          } else {
            newName += name[i];
          }
        }
        name = newName;

        // Texto QR completo como en FotoCheckPdf
        const qrText = `${this.worker.PrsDocumentNumber},${this.worker.TypeCard || ""},${
          this.worker.CrdVersion || "1"
        },${this.worker.NtpID || ""},${name},${this.worker.CtrID || ""}`;

        const url = await QRCode.toDataURL(qrText, {
          width: 180,
          margin: 0,
          color: {
            dark: "#000000",
            light: "#FFFFFF",
          },
        });
        this.qrCodeImage = url;
      } catch (error) {
        console.error("Error generando QR:", error);
        // Fallback: usar solo DNI
        try {
          const QRCode = require("qrcode");
          const url = await QRCode.toDataURL(this.worker.PrsDocumentNumber, {
            width: 260,
            margin: 2,
          });
          this.qrCodeImage = url;
        } catch (err) {
          console.error("Error generando QR fallback:", err);
        }
      }
    },

    async drawLogo(ctx) {
      // Logo Sunshine (usando require como en PdfVacation)
      return new Promise((resolve) => {
        const logo = new Image();
        logo.crossOrigin = "Anonymous";
        logo.onload = () => {
          // Dibujar logo centrado y más grande
          ctx.drawImage(logo, 150, 30, 300, 75); // (logo , x,y , width, height)
          resolve();
        };
        logo.onerror = () => {
          console.warn("Error cargando logo");
          resolve();
        };
        // Usar require para obtener la ruta correcta
        try {
          logo.src = require("@/assets/logo_sp.png");
        } catch (e) {
          // Fallback a ruta estática
          logo.src = "/static/logo_sp.png";
        }
      });
    },

    async drawPhoto(ctx) {
      if (!this.personImage) return;

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const centerX = 300;
          const centerY = 330;
          const radius = 190;

          // Dibujar borde según el tipo seleccionado
          ctx.beginPath();

          if (this.photoBorderType === "circle") {
            // Borde circular
            ctx.arc(centerX, centerY, radius + 3, 0, Math.PI * 2);
            ctx.lineWidth = 15;
            ctx.strokeStyle = this.photoBorderColor;
            ctx.stroke();
          } else {
            // Borde redondeado (cuadrado con esquinas redondeadas)
            const rectX = centerX - radius - 3 + radius / 4;
            const rectY = centerY - radius - 3;
            const rectW = (radius + 3) * 2 - radius / 2;
            // const rectW = (radius+3) * 2;
            const rectH = (radius + 3) * 2;
            const corner = 5;

            ctx.moveTo(rectX + corner, rectY);
            ctx.lineTo(rectX + rectW - corner, rectY);
            ctx.arcTo(rectX + rectW, rectY, rectX + rectW, rectY + corner, corner);
            ctx.lineTo(rectX + rectW, rectY + rectH - corner);
            ctx.arcTo(
              rectX + rectW,
              rectY + rectH,
              rectX + rectW - corner,
              rectY + rectH,
              corner
            );
            ctx.lineTo(rectX + corner, rectY + rectH);
            ctx.arcTo(rectX, rectY + rectH, rectX, rectY + rectH - corner, corner);
            ctx.lineTo(rectX, rectY + corner);
            ctx.arcTo(rectX, rectY, rectX + corner, rectY, corner);

            ctx.lineWidth = 8;
            ctx.strokeStyle = this.photoBorderColor;
            ctx.stroke();
          }

          // Recortar la imagen según el tipo seleccionado
          ctx.save();
          ctx.beginPath();

          if (this.photoBorderType === "circle") {
            // Recorte circular
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
          } else {
            // Recorte redondeado
            const rectX = centerX - radius + radius / 4;
            const rectY = centerY - radius;
            const rectW = radius * 2 - radius / 2;
            const rectH = radius * 2;
            const corner = 5;

            ctx.moveTo(rectX + corner, rectY);
            ctx.lineTo(rectX + rectW - corner, rectY);
            ctx.arcTo(rectX + rectW, rectY, rectX + rectW, rectY + corner, corner);
            ctx.lineTo(rectX + rectW, rectY + rectH - corner);
            ctx.arcTo(
              rectX + rectW,
              rectY + rectH,
              rectX + rectW - corner,
              rectY + rectH,
              corner
            );
            ctx.lineTo(rectX + corner, rectY + rectH);
            ctx.arcTo(rectX, rectY + rectH, rectX, rectY + rectH - corner, corner);
            ctx.lineTo(rectX, rectY + corner);
            ctx.arcTo(rectX, rectY, rectX + corner, rectY, corner);
          }

          ctx.clip();
          // COVER (la imagen cubre todo el área, puede recortarse)
          const diameter = radius * 2;
          if (this.photoBorderType === "circle") {
            const scaleCover = Math.max(diameter / img.width, diameter / img.height);
            const drawWidthCover = img.width * scaleCover;
            const drawHeightCover = img.height * scaleCover;
            const offsetXCover = centerX - drawWidthCover / 2;
            const offsetYCover = centerY - drawHeightCover / 2;
            ctx.drawImage(
              img,
              offsetXCover,
              offsetYCover,
              drawWidthCover,
              drawHeightCover
            );
          } else {
            // CONTAINER (la imagen se ajusta dentro del área, puede dejar espacio en los bordes)
            const scaleContainer = Math.min(diameter / img.width, diameter / img.height);
            const drawWidthContainer = img.width * scaleContainer;
            const drawHeightContainer = img.height * scaleContainer;
            const offsetXContainer = centerX - drawWidthContainer / 2;
            const offsetYContainer = centerY - drawHeightContainer / 2;
            ctx.drawImage(
              img,
              offsetXContainer,
              offsetYContainer,
              drawWidthContainer,
              drawHeightContainer
            );
          }
          ctx.restore();

          resolve();
        };
        img.onerror = () => {
          console.warn("Error cargando foto del trabajador");
          resolve();
        };
        img.src = this.personImage;
      });
    },

    drawName(ctx) {
      const firstName = this.$fun.capitalizeWords(this.worker.NtpName);
      const lastName = this.$fun.capitalizeWords(this.worker.NtpSurname);
      ctx.textAlign = "center";
      ctx.fillStyle = "#529518";
      ctx.font = "bold 40px Arial";
      ctx.fillText(firstName, 300, 580);
      ctx.fillText(lastName, 300, 620);
    },

    drawPosition(ctx) {
      const position = this.worker.PstName || this.worker.AreName || "";
      //   const positionParts = position.split(" ");

      ctx.textAlign = "center";
      ctx.fillStyle = "#529518";
      ctx.font = "bold 22px Arial";
      //   if (positionParts.length > 2) {
      //     const firstPart = positionParts
      //       .slice(0, Math.ceil(positionParts.length / 2))
      //       .join(" ");
      //     const secondPart = positionParts
      //       .slice(Math.ceil(positionParts.length / 2))
      //       .join(" ");
      //     ctx.fillText(firstPart.toUpperCase(), 150, 300);
      //     ctx.fillText(secondPart.toUpperCase(), 150, 320);
      //   } else {
      ctx.fillText(position.toUpperCase(), 300, 658);
      //   }
    },

    async drawQRCode(ctx) {
      if (!this.qrCodeImage) return;

      return new Promise((resolve) => {
        const qr = new Image();
        qr.onload = () => {
          const qrSize = 160;
          const paddingX = 2; // Padding horizontal
          const paddingY = 5; // Padding vertical
          const qrX = 215 - paddingX; // Ajustar para el padding
          const qrY = 680 - paddingY;

          // Bordes naranjas a los lados del QR con padding
          const borderWidth = 13;
          const borderHeight = qrSize + paddingY * 2;
          const borderOffset = 10;

          // Borde izquierdo naranja
          ctx.fillStyle = "#F35217";
          // Borde izquierdo naranja con esquinas redondeadas solo a la izquierda
          ctx.beginPath();
          // Empezamos en el punto después de la curva superior izquierda (parte superior, lado izquierdo)
          ctx.moveTo(qrX - borderOffset - borderWidth + 15, qrY);
          // Línea recta hacia la derecha (lado superior)
          ctx.lineTo(qrX - borderOffset, qrY);
          // Línea recta hacia abajo (lado derecho)
          ctx.lineTo(qrX - borderOffset, qrY + borderHeight);
          // Línea recta hacia la izquierda (lado inferior) hasta el inicio de la curva inferior izquierda
          ctx.lineTo(qrX - borderOffset - borderWidth + 15, qrY + borderHeight);
          // Curva inferior izquierda
          ctx.quadraticCurveTo(
            qrX - borderOffset - borderWidth,
            qrY + borderHeight,
            qrX - borderOffset - borderWidth,
            qrY + borderHeight - 15
          );
          // Línea recta hacia arriba (lado izquierdo) hasta el inicio de la curva superior izquierda
          ctx.lineTo(qrX - borderOffset - borderWidth, qrY + 15);
          // Curva superior izquierda
          ctx.quadraticCurveTo(
            qrX - borderOffset - borderWidth,
            qrY,
            qrX - borderOffset - borderWidth + 15,
            qrY
          );
          ctx.closePath();
          ctx.fill();

          // Borde derecho naranja con esquinas redondeadas solo a la derecha
          ctx.beginPath();
          // Empezamos en el lado izquierdo, esquina superior
          ctx.moveTo(qrX + qrSize + paddingX * 2 + borderOffset, qrY);
          // Línea recta hacia la derecha (lado superior) hasta el inicio de la curva superior derecha
          ctx.lineTo(qrX + qrSize + paddingX * 2 + borderOffset + borderWidth - 15, qrY);
          // Curva superior derecha
          ctx.quadraticCurveTo(
            qrX + qrSize + paddingX * 2 + borderOffset + borderWidth,
            qrY,
            qrX + qrSize + paddingX * 2 + borderOffset + borderWidth,
            qrY + 15
          );
          // Línea recta hacia abajo (lado derecho) hasta el inicio de la curva inferior derecha
          ctx.lineTo(
            qrX + qrSize + paddingX * 2 + borderOffset + borderWidth,
            qrY + borderHeight - 15
          );
          // Curva inferior derecha
          ctx.quadraticCurveTo(
            qrX + qrSize + paddingX * 2 + borderOffset + borderWidth,
            qrY + borderHeight,
            qrX + qrSize + paddingX * 2 + borderOffset + borderWidth - 5,
            qrY + borderHeight
          );
          // Línea recta hacia la izquierda (lado inferior)
          ctx.lineTo(qrX + qrSize + paddingX * 2 + borderOffset, qrY + borderHeight);
          // Línea recta hacia arriba (lado izquierdo) hasta el punto inicial
          ctx.lineTo(qrX + qrSize + paddingX * 2 + borderOffset, qrY);
          ctx.closePath();
          ctx.fill();

          // Dibujar QR con padding
          ctx.drawImage(qr, qrX + paddingX, qrY + paddingY, qrSize, qrSize);
          resolve();
        };
        qr.onerror = () => resolve();
        qr.src = this.qrCodeImage;
      });
    },

    drawBottomBar(ctx) {
      // Barra naranja inferior más delgada
      const barHeight = 61;
      const gradient = ctx.createLinearGradient(0, 1050 - barHeight, 650, 1050);
      gradient.addColorStop(0, "#F05502");
      //   gradient.addColorStop(0.5, "#F78738");
      gradient.addColorStop(1, "#F7AE38");

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 950 - barHeight, 600, barHeight);
    },
  },
};
</script>

<style scoped>
canvas {
  border: 1px solid #ddd;
}
</style>
